import { useGetMtoRunComparisonQuery } from '@shared/api';
import { fmtDate } from '@entities/mto';
import { ComparisonRowView } from './components/ComparisonRowView';
import { GroupsSection } from './components/GroupsSection';
import { UnmatchedSection } from './components/UnmatchedSection';
import { Muted, Table, TableScroll, WindowNote } from './styles';

/**
 * Сравнение КП по позициям прогона.
 *
 * `onError` поднимает текст наверх, в общий ErrBox карточки прогона — так же,
 * как это делают остальные HITL-секции раздела.
 */
export function ComparisonSection({
  runId,
  canWrite,
  onError,
}: {
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const { data, isLoading, isError } = useGetMtoRunComparisonQuery({ id: runId });

  if (isLoading) return <Muted>Собираем сравнение…</Muted>;
  if (isError || !data) return <Muted>Не удалось собрать сравнение предложений.</Muted>;
  if (data.rows.length === 0 && data.groups.length === 0 && data.unmatched.length === 0) {
    return <Muted>Сравнивать пока нечего — в базе нет предложений по позициям этой обработки.</Muted>;
  }

  return (
    <>
      <WindowNote>
        Цены собраны по всей базе КП с {fmtDate(data.since)} — не только из этой обработки.
        Просмотрено предложений: {data.offers_total}.
      </WindowNote>

      {data.rows.length > 0 ? (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th scope="col">Позиция</th>
                <th scope="col" className="num">
                  Предложений
                </th>
                <th scope="col" className="num">
                  Минимум
                </th>
                <th scope="col" className="num">
                  Разброс
                </th>
                <th scope="col" className="num">
                  Тренд
                </th>
                <th scope="col">Решение</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <ComparisonRowView
                  key={row.nomenclature_id}
                  row={row}
                  runId={runId}
                  canWrite={canWrite}
                  onError={onError}
                />
              ))}
            </tbody>
          </Table>
        </TableScroll>
      ) : data.groups.length === 0 ? (
        <Muted>Ни одна позиция не привязана к справочнику — сравнивать нечего.</Muted>
      ) : null}

      {data.groups.length > 0 && (
        <GroupsSection groups={data.groups} runId={runId} canWrite={canWrite} onError={onError} />
      )}

      {data.unmatched.length > 0 && (
        <UnmatchedSection
          items={data.unmatched}
          runId={runId}
          canWrite={canWrite}
          onError={onError}
        />
      )}
    </>
  );
}

export default ComparisonSection;
